import { BoothStatusData } from '@cibli/shared';
import { useMicdropState } from '@micdrop/react';
import { useState, useEffect, useMemo } from 'react';

import { DeviceInfoLocalService } from '@/bridge/DeviceInfoLocalService';
import { NetworkInfoLocalService } from '@/bridge/NetworkInfoLocalService';
import usePermissionsMediaDevices from '@/features/settings/hooks/usePermissionsMediaDevices';
import { usePrinter } from '@/features/settings/hooks/usePrinter';

type BoothStatusRef = Partial<BoothStatusData> & {
  loading: boolean;
  error: string | null;
};

const initialBoothStatus: BoothStatusRef = {
  loading: true,
  error: null,
  system: undefined,
  network: undefined,
  devices: undefined,
  printing: undefined,
};

export function useSystem() {
  const [boothStatus, setBoothStatus] = useState<BoothStatusRef>(initialBoothStatus);

  const { micDeviceId, speakerDeviceId } = useMicdropState();
  const { devices: mediaDevices, requestPermissions: requestDevicesPermissions } =
    usePermissionsMediaDevices();
  const { printerAvailable, printers, printerIP } = usePrinter();

  const isPrintMode = useMemo(() => {
    return new URLSearchParams(window.location.search).get('print') === '1';
  }, []);

  useEffect(() => {
    setBoothStatus(
      prev =>
        ({
          ...prev,
          printing: {
            printerAvailable,
            printers,
            printerIP,
          },
        }) as BoothStatusRef,
    );
  }, [printerAvailable, printers, printerIP]);

  useEffect(() => {
    (async () => {
      try {
        setBoothStatus(
          prev =>
            ({
              ...prev,
              loading: true,
              error: null,
            }) as BoothStatusRef,
        );

        const [info, langCode, networkInfo] = await Promise.all([
          DeviceInfoLocalService.getDeviceInfo(),
          DeviceInfoLocalService.getLanguageCode(),
          NetworkInfoLocalService.getNetworkInfo(),
        ]);

        setBoothStatus(
          prev =>
            ({
              ...prev,
              system: {
                ...info,
                languageCode: langCode,
              },
              network: networkInfo,
              loading: false,
            }) as BoothStatusRef,
        );
      } catch (err) {
        console.error('[useSystem] Error getting system info', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setBoothStatus(
          prev =>
            ({
              ...prev,
              error: errorMessage,
              loading: false,
            }) as BoothStatusRef,
        );
      }
    })();
  }, []);

  useEffect(() => {
    // Skip media device permissions in print mode (Puppeteer PDF generation)
    if (!isPrintMode) {
      // Do not await, that's taking some time
      requestDevicesPermissions();
    }
  }, [requestDevicesPermissions, isPrintMode]);

  useEffect(() => {
    let firstCamera = true;
    const devices = (mediaDevices ?? []).map(device => {
      let active: boolean = false;
      switch (device.kind) {
        case 'audioinput':
          active = device.deviceId === micDeviceId;
          break;
        case 'audiooutput':
          active = device.deviceId === speakerDeviceId;
          break;
        case 'videoinput':
          active = firstCamera === true;
          firstCamera = false;
          break;
      }
      return {
        deviceId: device.deviceId,
        label: device.label,
        kind: device.kind,
        active: active,
      };
    });
    setBoothStatus(
      prev =>
        ({
          ...prev,
          devices,
        }) as BoothStatusRef,
    );
  }, [mediaDevices, micDeviceId, speakerDeviceId]);

  return {
    boothStatus,
    isPrintMode,
  };
}
